import { faClose } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames, { Value as ClassValue } from "classnames";
import React, { CSSProperties, useState } from "react";
import { useAsync } from "react-use";
import { hasValue } from "Tools";
import {
  createFieldProps,
  SimpleFieldProps,
  useSimpleFieldProps,
} from "./FormTools";
import { Textbox } from "./Textbox";

interface AutocompleteItem {
  key?: string;
  value: string;
  className?: ClassValue;
  style?: CSSProperties;
  isDisabled?: boolean;
  children?: React.ReactNode;
}

const noResultsOption = {
  value: "No results found.",
  className: "has-color-danger",
  isDisabled: true,
};

export const loadingOption = {
  value: "Loading...",
  isDisabled: true,
};

export function Wordwheel(props: {
  fieldProps: SimpleFieldProps<string | null>;
  placeholder?: string;
  className?: string;
  leftIconContent?: React.ReactNode;
  isDisabled?: boolean;
  style?: CSSProperties;
  autocompleteItems?: (AutocompleteItem & {
    onClick?: () => void;
  })[];
  blurredInputProps?: {
    type?: string;
    autoComplete?: string;
  };
  footer?: React.ReactNode;
}) {
  const autocompleteItems: typeof props.autocompleteItems =
    props.autocompleteItems &&
    (props.autocompleteItems.length > 0
      ? props.autocompleteItems
      : [noResultsOption]);
  const field = useSimpleFieldProps(props.fieldProps);
  const [focusedValue, setFocusedValue] = useState("");
  const inputPropsOverride =
    focusedValue === "" ? props.blurredInputProps ?? {} : {};
  return (
    <div style={props.style}>
      <Textbox
        type={inputPropsOverride.type ?? "search"}
        fieldProps={props.fieldProps}
        placeholder={props.placeholder}
        className={classNames(props.className, "hide-clear")}
        leftIconContent={props.leftIconContent}
        rightIconContent={
          <FontAwesomeIcon
            icon={faClose}
            onClick={async () => {
              setFocusedValue("");
              await field.onChange(null);
            }}
            style={{ pointerEvents: "initial" }}
            className={classNames("click-catcher-small", {
              "is-hidden": !hasValue(field.value),
            })}
          />
        }
        isDisabled={props.isDisabled}
        style={props.style}
        inputProps={{
          autoComplete: inputPropsOverride.autoComplete ?? "off",
        }}
        setFocusedValue={setFocusedValue}
      />
      <div
        className={classNames("dropdown is-flex", {
          "is-active": focusedValue !== "" && hasValue(autocompleteItems),
        })}
        style={{ width: "100%" }}
      >
        <div className="dropdown-menu">
          <div className="dropdown-content">
            {autocompleteItems &&
              autocompleteItems.map(e => (
                <div
                  key={e.key ?? e.value}
                  style={e.style}
                  className={classNames("dropdown-item", e.className)}
                  onMouseDown={async () => {
                    if (e.isDisabled === true) {
                      return;
                    }
                    await field.onChange(e.value);
                    e.onClick?.();
                  }}
                >
                  {e.children ?? e.value}
                </div>
              ))}
            {props.footer}
          </div>
        </div>
      </div>
    </div>
  );
}

export function SearchWordwheel(props: {
  placeholder?: string;
  className?: string;
  type?: string;
  leftIconContent?: React.ReactNode;
  isDisabled?: boolean;
  style?: CSSProperties;

  getAutocompleteItems: (search: string) => Promise<
    (AutocompleteItem & {
      onClick?: (clearSearch: () => void) => void;
    })[]
  >;
}) {
  const [search, setSearch] = useState<string | null>(null);
  const autocompleteItemsResult = useAsync(
    () => props.getAutocompleteItems(search ?? ""),
    [search],
  );
  return (
    <Wordwheel
      {...props}
      fieldProps={createFieldProps({
        name: "search",
        value: search,
        setValue: setSearch,
      })}
      autocompleteItems={
        autocompleteItemsResult.loading
          ? [loadingOption]
          : autocompleteItemsResult.value?.map(item => ({
              ...item,
              onClick: () => {
                item.onClick?.(() => setSearch(null));
              },
            }))
      }
    />
  );
}
